/**
 * The typed client. One host, so no CORS story exists.
 *
 * app.easting.ai proxies `/v1/*`, `/billing/*`, `/account/*` and `/healthz` to
 * the same Lambda origin api.easting.ai fronts, which is why every path here
 * is relative. Nothing in this file knows the API's hostname, and there is no
 * preflight anywhere in the app.
 *
 * **`extract` returns the raw text alongside the parsed object, on purpose.**
 * The attestation is an HMAC over the serialized response body, and
 * `/v1/certificate` verifies the bytes it receives rather than a re-validated
 * model. Posting `JSON.stringify(parsed)` would work right up until a server
 * adds a field this build drops, at which point every certificate stops
 * verifying. Holding the string costs a few kilobytes and removes the whole
 * class of problem.
 */

import type {
  CustomerRow,
  EventDetail,
  JobsView,
  KeyDetail,
  MintedKey,
  PollResult,
  ReconcileResult,
  UsageView,
} from "./admin/types";
import { sliceArrayElements, sliceMember } from "./rawjson";
import type {
  BatchResult,
  BatchStatus,
  Correction,
  ExtractJob,
  ExtractResponse,
  HeldResponse,
  KeyRow,
  MintResponse,
  PlacementListResponse,
  RunListResponse,
  SavedPlacement,
  WhoAmI,
} from "./types";

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly type: string,
    message: string,
  ) {
    super(message);
  }

  /** Over quota. The message is the server's own, which names the plan, the
   *  reset date and the way out; paraphrasing it here would lose all three. */
  get quotaExceeded(): boolean {
    return this.status === 402;
  }
}

/** What a pasted legal description may weigh, in UTF-8 bytes. Mirrors the
 *  service's own ceiling so the refusal happens before the request does. */
export const MAX_TEXT_BYTES = 64 * 1024;

/** Supplies the bearer token and is told when the server rejects one. */
export interface TokenSource {
  token(): Promise<string | null>;
  onUnauthorized(): void;
}

export class Api {
  constructor(private readonly tokens: TokenSource) {}

  private async headers(extra: Record<string, string> = {}): Promise<Headers> {
    const headers = new Headers(extra);
    const token = await this.tokens.token();
    if (token) headers.set("authorization", `Bearer ${token}`);
    return headers;
  }

  /** The authenticated fetch, for `AdminApi` to reuse. Same token source,
   *  same 401 handling, no second copy of either. */
  raw(path: string, init: RequestInit = {}): Promise<Response> {
    return this.request(path, init);
  }

  private async request(
    path: string,
    init: RequestInit = {},
    { endsSessionOn401 = true }: { endsSessionOn401?: boolean } = {},
  ): Promise<Response> {
    const response = await fetch(path, {
      ...init,
      headers: await this.headers(
        Object.fromEntries(new Headers(init.headers).entries()),
      ),
    });
    if (response.status === 401 && endsSessionOn401) {
      // The token expired or was revoked. Auth refreshes ahead of expiry, so
      // reaching here means the session is genuinely over.
      this.tokens.onUnauthorized();
    }
    if (!response.ok) throw await toError(response);
    return response;
  }

  /**
   * Who is signed in, which decides what to draw and never what is allowed.
   *
   * A 401 here does not end the session, and that exception is the whole
   * point: the route answers for a pool session, so an `east_live_` key gets
   * a legitimate 401 from it while remaining a perfectly good credential for
   * extraction. Treating that as an expired session signed the local
   * pasted-key door straight back out the moment it signed in, which broke
   * `task portal:e2e` silently from 2026-08-11 until 2026-08-16 (the browser
   * Playwright needs was never installed, so the suite failed on a missing
   * executable and nobody reached the real bug).
   */
  async whoami(): Promise<WhoAmI> {
    return (
      await this.request("/v1/keys/whoami", {}, { endsSessionOn401: false })
    ).json();
  }

  /** What this account has run, across every key it owns. */
  async runs(limit = 100): Promise<RunListResponse> {
    return (await this.request(`/v1/runs?limit=${limit}`)).json();
  }

  async keys(): Promise<KeyRow[]> {
    const body = await (await this.request("/v1/keys")).json();
    return body.keys ?? [];
  }

  async mintKey(name: string): Promise<MintResponse> {
    const response = await this.request("/v1/keys", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ name }),
    });
    return response.json();
  }

  /** New secret, same name and allowance, old key revoked, in one server-side
   *  transaction. The recovery path for a plaintext nobody wrote down. */
  async rotateKey(prefix: string): Promise<MintResponse> {
    const response = await this.request(
      `/v1/keys/${encodeURIComponent(prefix)}/rotate`,
      { method: "POST" },
    );
    return response.json();
  }

  async revokeKey(prefix: string): Promise<void> {
    await this.request(`/v1/keys/${encodeURIComponent(prefix)}`, {
      method: "DELETE",
    });
  }

  /**
   * Submit one document and get a job id back in milliseconds.
   *
   * Always the async route, never `/v1/extract`. The sync endpoint is right
   * for a script curling one small deed and wrong for a browser: a
   * three-tract easement in the corpus runs 270 seconds and 504s at the
   * edge's 180-second ceiling, having already completed and billed. The user
   * would watch a spinner turn into an error for work they paid for.
   */
  async submit(file: File, uploadId?: string): Promise<ExtractJob> {
    const headers: Record<string, string> = { "x-document-name": file.name };
    let body: BodyInit;
    if (uploadId) {
      headers["x-upload-id"] = uploadId;
      headers["content-type"] = "application/json";
      body = "{}";
    } else {
      headers["content-type"] = "application/pdf";
      body = file;
    }
    const response = await this.request("/v1/extracts", {
      method: "POST",
      headers,
      body,
    });
    return response.json();
  }

  /**
   * Submit a pasted legal description instead of a file.
   *
   * Same async route, same poll; the service reads the kind from the bytes
   * and labels the result `input_kind: "text"`, which the certificate prints
   * as its Source line. The size check runs here because refusing before the
   * request is free and refusing after it costs a round trip.
   */
  async submitText(text: string, docName: string): Promise<ExtractJob> {
    const body = new TextEncoder().encode(text);
    if (body.byteLength > MAX_TEXT_BYTES) {
      throw new ApiError(
        413,
        "text_too_large",
        `This description is ${Math.round(body.byteLength / 1024)} KB of ` +
          `text; the limit is ${MAX_TEXT_BYTES / 1024} KB.`,
      );
    }
    const response = await this.request("/v1/extracts", {
      method: "POST",
      headers: {
        "content-type": "text/plain; charset=utf-8",
        "x-document-name": docName || "pasted-text",
      },
      body,
    });
    return response.json();
  }

  /**
   * Delete one run's result. The row survives; the extraction does not.
   *
   * A batch reference clears every document in that batch, which is what the
   * listing's reference means, so the caller confirms before reaching here.
   */
  async deleteRun(reference: string): Promise<number> {
    const response = await this.request(
      `/v1/runs/${encodeURIComponent(reference)}`,
      { method: "DELETE" },
    );
    const body = (await response.json()) as { cleared: number };
    return body.cleared;
  }

  /**
   * Where this run's tracts were placed, or an empty list if nowhere yet.
   *
   * Read on open, so a deed placed last Tuesday comes back on the map
   * instead of at the origin.
   */
  async placements(reference: string): Promise<SavedPlacement[]> {
    const response = await this.request(
      `/v1/runs/${encodeURIComponent(reference)}/placements`,
    );
    const body = (await response.json()) as PlacementListResponse;
    return body.placements ?? [];
  }

  /**
   * Record where these tracts sit. A tract left out keeps what it had.
   *
   * `document` names which deed of a folder run the positions belong to, and
   * is not needed for a single extraction: one reference, one document, and
   * the service already knows its name.
   */
  async savePlacements(
    reference: string,
    tracts: SavedPlacement[],
    document = "",
  ): Promise<SavedPlacement[]> {
    const query = document ? `?document=${encodeURIComponent(document)}` : "";
    const response = await this.request(
      `/v1/runs/${encodeURIComponent(reference)}/placements${query}`,
      {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ tracts }),
      },
    );
    const body = (await response.json()) as PlacementListResponse;
    return body.placements ?? [];
  }

  /** Drop this run's saved positions and keep its extraction. */
  async forgetPlacements(reference: string): Promise<number> {
    const response = await this.request(
      `/v1/runs/${encodeURIComponent(reference)}/placements`,
      { method: "DELETE" },
    );
    const body = (await response.json()) as { placements: number };
    return body.placements;
  }

  /** Clear every result this account still holds. */
  async purgeRuns(): Promise<number> {
    const response = await this.request("/v1/runs?confirm=delete-everything", {
      method: "DELETE",
    });
    const body = (await response.json()) as { cleared: number };
    return body.cleared;
  }

  async pollExtract(
    jobId: string,
  ): Promise<{ job: ExtractJob; held: HeldResponse | null }> {
    const response = await this.request(
      `/v1/extracts/${encodeURIComponent(jobId)}`,
    );
    const raw = await response.text();
    const job = JSON.parse(raw) as ExtractJob & { result?: ExtractResponse };
    // The finished job embeds the whole extract response, and the certificate
    // needs it as the server wrote it. `JSON.stringify(job.result)` would
    // print 1758.0 as 1758, the server would canonicalize that as an integer,
    // and the attestation would not match.
    const slice =
      job.status === "succeeded" && job.result
        ? sliceMember(raw, "result")
        : null;
    const held =
      job.result && slice ? { parsed: job.result, raw: slice } : null;
    return { job, held };
  }

  /**
   * A presigned slot for anything past the direct ceiling.
   *
   * The Function URL base64-encodes request bodies, which caps a direct
   * upload near 4.5 MB. Bigger documents PUT straight to the bucket, whose
   * CORS already allows it from any origin, and the extraction request then
   * names the upload id.
   */
  async stage(file: File): Promise<string> {
    const response = await this.request("/v1/uploads", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ filename: file.name, content_length: file.size }),
    });
    const slot = await response.json();
    const put = await fetch(slot.url, {
      method: "PUT",
      headers: { "content-type": "application/octet-stream" },
      body: file,
    });
    if (!put.ok)
      throw new ApiError(
        put.status,
        "upload_failed",
        "The upload did not complete.",
      );
    return slot.upload_id;
  }

  async submitBatch(files: File[]): Promise<BatchStatus> {
    const form = new FormData();
    for (const file of files) form.append("files", file, file.name);
    return (
      await this.request("/v1/batches", { method: "POST", body: form })
    ).json();
  }

  async batch(batchId: string): Promise<BatchStatus> {
    return (
      await this.request(`/v1/batches/${encodeURIComponent(batchId)}`)
    ).json();
  }

  /**
   * Collect a finished batch.
   *
   * Fetching starts no clock: results stay on the server until somebody
   * deletes them. The portal still keeps them in memory for the session
   * rather than re-fetching a page it already showed.
   */
  async batchResults(batchId: string): Promise<HeldResponse[]> {
    const response = await this.request(
      `/v1/batches/${encodeURIComponent(batchId)}/results`,
    );
    const raw = await response.text();
    const body = JSON.parse(raw) as { results?: BatchResult[] };
    // The same reserialization trap as the single path, one level deeper:
    // each element's `result` has to travel as the source text it arrived as.
    const rawResults = sliceMember(raw, "results");
    const elements = rawResults ? sliceArrayElements(rawResults) : [];
    const held: HeldResponse[] = [];
    (body.results ?? []).forEach((entry, index) => {
      // A failed document carries `error` instead of a result; an empty
      // review panel for it would hide the failure.
      if (!entry.result) return;
      const slice = elements[index]
        ? sliceMember(elements[index], "result")
        : null;
      if (slice) held.push({ parsed: entry.result, raw: slice });
    });
    return held;
  }

  /** Post the response back, byte for byte, and get the PDF. */
  async certificate(held: HeldResponse): Promise<Blob> {
    const response = await this.request("/v1/certificate", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: held.raw,
    });
    return response.blob();
  }

  /**
   * Ask where the tracts sit, and take back the whole re-signed response.
   *
   * The suggestions cannot be spliced into the response already held: the
   * attestation covers the body as a unit, so a spliced payload is one the
   * certificate endpoint refuses. The caller replaces what it holds, and the
   * raw bytes it keeps are the ones the server just signed.
   */
  async locate(
    held: HeldResponse,
    // "auto" lets the service try everything it knows: a commencement corner
    // it can resolve, or a boundary shared with a parcel already placed.
    // Naming a source restricts it to that one.
    source = "auto",
  ): Promise<{ held: HeldResponse; located: number }> {
    const response = await this.request("/v1/locate", {
      method: "POST",
      headers: { "content-type": "application/json" },
      // The extraction rides inside the envelope as the exact bytes it
      // arrived as, never JSON.parse-then-stringify. JavaScript serializes
      // whole-number floats without the decimal point (0.0 becomes 0),
      // Python parses that as an int and canonicalizes it differently, and
      // the attestation stops verifying on every real payload. Found in
      // prod on the first click.
      body: `{"extraction":${held.raw},"source":${JSON.stringify(source)}}`,
    });
    const body = await response.text();
    const answer = JSON.parse(body) as {
      extraction: ExtractResponse;
      located: number;
    };
    return {
      held: {
        parsed: answer.extraction,
        raw: sliceMember(body, "extraction") ?? "",
      },
      located: answer.located,
    };
  }

  /**
   * Correct what the service misread, or fit courses that do not close.
   *
   * Two different asks down one endpoint, and the response keeps them apart:
   * a correction is a better reading of the document, so the verdicts come
   * back recomputed over it; an adjustment fits geometry and the verdict
   * deliberately does not move.
   *
   * Same swap as `locate`, for the same reason: the attestation covers the
   * body as a unit, so nothing can be spliced into what is already held. The
   * caller replaces its payload with the one the server just signed.
   */
  async correct(
    held: HeldResponse,
    changes: { corrections?: Correction[]; adjust?: number[]; by?: string },
  ): Promise<{ held: HeldResponse; corrected: number; adjusted: number }> {
    const rest = JSON.stringify({
      corrections: changes.corrections ?? [],
      adjust: changes.adjust ?? [],
      by: changes.by ?? "",
    }).slice(1);
    const response = await this.request("/v1/corrections", {
      method: "POST",
      headers: { "content-type": "application/json" },
      // The extraction rides as the exact bytes it arrived as. JavaScript
      // writes whole-number floats without the decimal point (0.0 becomes 0),
      // Python parses that as an int and canonicalizes it differently, and
      // the attestation stops verifying. Found in prod on the first click.
      body: `{"extraction":${held.raw},${rest}`,
    });
    const body = await response.text();
    const answer = JSON.parse(body) as {
      extraction: ExtractResponse;
      corrected: number;
      adjusted: number;
    };
    return {
      held: {
        parsed: answer.extraction,
        raw: sliceMember(body, "extraction") ?? "",
      },
      corrected: answer.corrected,
      adjusted: answer.adjusted,
    };
  }

  async dxf(held: HeldResponse): Promise<Blob> {
    const response = await this.request("/v1/dxf", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: held.raw,
    });
    return response.blob();
  }

  /** Server-written GeoPackage: browsers cannot write SQLite, so the same
   *  byte-exact post-back that fetches the DXF fetches this. */
  async geopackage(held: HeldResponse): Promise<Blob> {
    const response = await this.request("/v1/geopackage", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: held.raw,
    });
    return response.blob();
  }

  /**
   * Every parcel this account has located, as one GeoPackage.
   *
   * The only export that is not a transform of something the caller posts
   * back: the service assembles it from what it holds, across every key the
   * account owns, so the deeds placed in QGIS and the ones placed here arrive
   * on one map.
   */
  async fabric(): Promise<Blob> {
    return (await this.request("/v1/fabric")).blob();
  }
}

/**
 * The operator console, over the same client.
 *
 * `/v1/admin/*` rather than `/admin/*`: this host proxies `/v1/*` to the API
 * and rewrites everything else to the SPA, so `/admin` here is the console
 * page. A second class rather than more methods on `Api`, because nothing a
 * customer's session does should be one typo away from a route that reads
 * every customer's row.
 */
export class AdminApi {
  constructor(private readonly api: Api) {}

  private request(path: string, init?: RequestInit) {
    return this.api.raw(`/v1/admin${path}`, init);
  }

  private async json<T>(path: string, init?: RequestInit): Promise<T> {
    return (await this.request(path, init)).json();
  }

  customers(): Promise<{ customers: CustomerRow[]; generated_at: string }> {
    return this.json("/customers");
  }

  keys(): Promise<{ keys: KeyDetail[] }> {
    return this.json("/keys");
  }

  mint(body: {
    name: string;
    monthly_cap: number;
    pool_docs?: number;
    owner_email?: string;
  }): Promise<MintedKey> {
    return this.json("/keys", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    });
  }

  rotate(prefix: string): Promise<MintedKey> {
    return this.json(`/keys/${encodeURIComponent(prefix)}/rotate`, {
      method: "POST",
    });
  }

  async revoke(prefix: string): Promise<void> {
    await this.request(`/keys/${encodeURIComponent(prefix)}`, {
      method: "DELETE",
    });
  }

  jobs(): Promise<JobsView> {
    return this.json("/jobs");
  }

  poll(): Promise<PollResult> {
    return this.json("/jobs/poll", { method: "POST" });
  }

  events(): Promise<{ events: EventDetail[] }> {
    return this.json("/events");
  }

  /** Defaults to a report. `apply` is the button that posts to Stripe. */
  reconcile(apply = false): Promise<ReconcileResult> {
    return this.json(`/reconcile?apply=${apply}`, { method: "POST" });
  }

  usage(): Promise<UsageView> {
    return this.json("/usage");
  }
}

async function toError(response: Response): Promise<ApiError> {
  let type = "http_error";
  let message = `The server answered ${response.status}.`;
  try {
    const body = await response.json();
    if (body?.error) {
      type = body.error.type ?? type;
      message = body.error.message ?? message;
    }
  } catch {
    // A non-JSON body means something upstream of the app answered: an edge
    // timeout, a proxy, a 502 page. The status is all there is to report.
  }
  return new ApiError(response.status, type, message);
}
